// timerConfigurationUtils.ts
// Utilities, types, constants and logic extracted from TimerConfigurationPanel

import { getTranslation } from "common/utilities";
import {
    generateCronExpression,
    parseCronExpression,
} from "utils/DateUtils";

// ─── Enums & Types ────────────────────────────────────────────────────────────

export enum EverySupported {
    year,
    month,
    week,
    day,
    hour,
    minute,
}

export interface CronData {
    every: EverySupported;
    minutes: number[];
    hours: number[];
    days: number[];
    months: number[];
    daysOfWeek: number[];
}

export type ActiveTab = "recurring" | "single";
export type BaseUnit = "minutes" | "hours" | "days" | "weeks" | "months";

/**
 * A "scheduled restriction" chip: one confirmed combination of
 * day-of-week / day-of-month / month filters + a specific time.
 * Multiple chips allow e.g. "Tue at 09:00" + "Thu at 11:00".
 */
export interface ScheduledRestriction {
    id: string;            // unique id (crypto.randomUUID or Date.now fallback)
    days: number[];        // day-of-week values (0=Sun … 6=Sat), [] = all
    daysOfMonth: number[]; // day-of-month values (1–31), [] = all
    months: number[];      // month values (1–12), [] = all
    time: string;          // "HH:mm"
    useFrequency?: boolean; // frequency enabled for this chip
    baseInterval?: number;  // frequency interval
    baseUnit?: BaseUnit;    // frequency unit
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

export const DEFAULT_CRONDATA: CronData = {
    every: EverySupported.year,
    days: [1],
    daysOfWeek: [],
    hours: [0],
    minutes: [0],
    months: [1],
};

// ─── Static Data ──────────────────────────────────────────────────────────────

export const WEEK_DAYS = [
    { id: "sun", value: 0, label: "D", full: "Domenica", name: "sunday" },
    { id: "mon", value: 1, label: "L", full: "Lunedì", name: "monday" },
    { id: "tue", value: 2, label: "M", full: "Martedì", name: "tuesday" },
    { id: "wed", value: 3, label: "M", full: "Mercoledì", name: "wednesday" },
    { id: "thu", value: 4, label: "G", full: "Giovedì", name: "thursday" },
    { id: "fri", value: 5, label: "V", full: "Venerdì", name: "friday" },
    { id: "sat", value: 6, label: "S", full: "Sabato", name: "saturday" },
];

export const MONTHS_UI = [
    { id: 1, label: "Gen", name: "january" },
    { id: 2, label: "Feb", name: "february" },
    { id: 3, label: "Mar", name: "march" },
    { id: 4, label: "Apr", name: "april" },
    { id: 5, label: "Mag", name: "may" },
    { id: 6, label: "Giu", name: "june" },
    { id: 7, label: "Lug", name: "july" },
    { id: 8, label: "Ago", name: "august" },
    { id: 9, label: "Set", name: "september" },
    { id: 10, label: "Ott", name: "october" },
    { id: 11, label: "Nov", name: "november" },
    { id: 12, label: "Dic", name: "december" },
];

export const UNIT_LABELS = (translations: any): Record<BaseUnit, string> => ({
    minutes: getTranslation(translations, "generic.minutes.caption") || "minuti",
    hours: getTranslation(translations, "generic.hours.caption") || "ore",
    days: getTranslation(translations, "generic.days.caption") || "giorni",
    weeks: getTranslation(translations, "pm.propertyeditor.specificproperties.repetition_details.duration.label.weeks") || "settimane",
    months: getTranslation(translations, "generic.months.caption") || "mesi",
});

// ─── Cron Parsing ─────────────────────────────────────────────────────────────

export interface ParsedCronState {
    crondata: CronData;
    baseUnit: BaseUnit;
    baseInterval: number;
    useFrequency: boolean;
    useMonthFilter: boolean;
    useDayFilter: boolean;
    useDayOfMonthFilter: boolean;
    selectedMonths: number[];
    selectedDays: number[];
    selectedDaysOfMonth: number[];
    startTime: string;
    /** Reconstructed chips (best-effort). When present, the panel uses chips mode. */
    scheduledRestrictions: ScheduledRestriction[];
}

export function parseCronStringToState(expression: string): ParsedCronState {
    // ── Check for pipe separator (multiple chips mode) ──────────────────────
    if (expression.includes("|")) {
        const parts = expression.split("|");
        const scheduledRestrictions: ScheduledRestriction[] = [];
        
        parts.forEach((part, index) => {
            if (!part.trim()) return;
            const parsed = parseCronExpression(part);
            const { selectedDays, selectedMonths, selectedDaysOfTheMonth, selectedHours, selectedMinutes } = parsed;
            
            // Reconstruct time
            const hour = selectedHours.length > 0 ? selectedHours[0] : 9;
            const minute = selectedMinutes.length > 0 ? selectedMinutes[0] : 0;
            const timeStr = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
            
            // Infer if it has frequency (if it has multiple hours/minutes or a specific every setting)
            // parseCronExpression expands ranges and every-steps into arrays.
            const hasFreq = selectedHours.length > 1 || selectedMinutes.length > 1 ||
                (selectedHours.length > 0 && selectedHours.length < 24 && part.includes("/")) ||
                (selectedMinutes.length > 0 && selectedMinutes.length < 60 && part.includes("/"));
                
            let bUnit: BaseUnit = "hours";
            let bInterval = 1;
            
            if (hasFreq) {
                if (selectedHours.length > 1) {
                    bUnit = "hours";
                    bInterval = selectedHours[1] - selectedHours[0];
                } else if (selectedMinutes.length > 1) {
                    bUnit = "minutes";
                    bInterval = selectedMinutes[1] - selectedMinutes[0];
                } else if (part.includes("/")) {
                    // Extract interval from string if only one value but has / step
                    const match = part.match(/\/(\d+)/);
                    if (match) bInterval = parseInt(match[1]);
                }
            }

            scheduledRestrictions.push({
                id: `${Date.now()}-${index}`,
                days: [...selectedDays],
                daysOfMonth: [...selectedDaysOfTheMonth],
                months: [...selectedMonths],
                time: timeStr,
                useFrequency: hasFreq,
                baseInterval: bInterval,
                baseUnit: bUnit
            });
        });
        
        // Return dummy base state, UI will work in chips mode
        return {
            crondata: DEFAULT_CRONDATA,
            baseUnit: "hours",
            baseInterval: 1,
            useFrequency: false,
            useMonthFilter: false,
            useDayFilter: false,
            useDayOfMonthFilter: false,
            selectedMonths: [],
            selectedDays: [],
            selectedDaysOfMonth: [],
            startTime: "00:00",
            scheduledRestrictions,
        };
    }

    // ── Single expression parsing (Legacy Mode) ──────────────────────────────
    const {
        selectedDays: daysOfWeek,
        selectedMinutes: minutes,
        selectedHours: hours,
        selectedMonths: months,
        selectedDaysOfTheMonth: days,
    } = parseCronExpression(expression);

    const every =
        months.length > 0 ? EverySupported.year :
            daysOfWeek.length > 0 ? EverySupported.month :
                days.length > 0 ? EverySupported.week :
                    hours.length > 0 ? EverySupported.day :
                        minutes.length > 0 ? EverySupported.hour :
                            EverySupported.minute;

    const crondata: CronData = { every, days, daysOfWeek, hours, minutes, months };

    let baseUnit: BaseUnit = "hours";
    if (every === EverySupported.year) baseUnit = "months";
    else if (every === EverySupported.month) baseUnit = "weeks";
    else if (every === EverySupported.week) baseUnit = "days";
    else if (every === EverySupported.day) baseUnit = "hours";
    else if (every === EverySupported.hour) baseUnit = "minutes";

    let baseInterval = 1;
    if (baseUnit === "hours" && hours.length > 1) baseInterval = hours[1] - hours[0];
    if (baseUnit === "minutes" && minutes.length > 1) baseInterval = minutes[1] - minutes[0];

    // If "Limit Weekdays" is ON but we don't have frequency, it's NOT frequency.
    // parseCronExpression expands 8am to 8. It only means frequency if length > 1
    const hasDayFilter = daysOfWeek.length > 0;
    const hasDayOfMonthFilter = days.length > 0;
    
    // Explicitly check for frequency only if we have more than 1 hour or minute entry
    const isFreq = hours.length > 1 || minutes.length > 1;

    const useFrequency = isFreq;

    const startTime =
        hours.length > 0 && minutes.length > 0
            ? `${String(hours[0]).padStart(2, "0")}:${String(minutes[0]).padStart(2, "0")}`
            : "00:00";

    // ── Best-effort chip reconstruction (Legacy Single Cron string with multiple times) ──
    let scheduledRestrictions: ScheduledRestriction[] = [];
    if (!useFrequency && hasDayFilter && hours.length > 0 && minutes.length > 0) {
        // ... (this logic was for when they saved Tue+Thu and it was one string, but now we use | )
        // Let's migrate them to chips anyway if there are multiple hours and NO frequency.
        // Actually, if we have frequency, it's just frequency. 
    }

    return {
        crondata,
        baseUnit,
        baseInterval,
        useFrequency,
        useMonthFilter: months.length > 0,
        useDayFilter: hasDayFilter,
        useDayOfMonthFilter: hasDayOfMonthFilter,
        selectedMonths: months,
        selectedDays: daysOfWeek,
        selectedDaysOfMonth: days,
        startTime,
        scheduledRestrictions,
    };
}

// ─── Cron Building ────────────────────────────────────────────────────────────

export function buildCronExpression(crondata: CronData): string {
    return generateCronExpression({
        selectedMonths: crondata.months.slice().sort((a, b) => a - b),
        selectedDays: crondata.daysOfWeek.slice().sort((a, b) => a - b),
        selectedDaysOfTheMonth: crondata.days.slice().sort((a, b) => a - b),
        selectedHours: crondata.hours.slice().sort((a, b) => a - b),
        selectedMinutes: crondata.minutes.slice().sort((a, b) => a - b),
    });
}

export function buildCronExpressionFromChips(
    chips: ScheduledRestriction[],
    fallbackStartTime = "00:00"
): string {
    if (chips.length === 0) {
        const [fh, fm] = fallbackStartTime.split(":").map(Number);
        return generateCronExpression({
            selectedMonths: [],
            selectedDays: [],
            selectedDaysOfTheMonth: [],
            selectedHours: [fh],
            selectedMinutes: [fm],
        });
    }

    // Map each chip to its own cron string
    const cronStrings = chips.map(chip => {
        const { hours, minutes } = computeUpdatedCronHoursMinutes(
            chip.time,
            chip.baseUnit || "hours",
            chip.baseInterval || 1,
            !!chip.useFrequency
        );
        
        return generateCronExpression({
            selectedMonths: chip.months.slice().sort((a, b) => a - b),
            selectedDays: chip.days.slice().sort((a, b) => a - b),
            selectedDaysOfTheMonth: chip.daysOfMonth.slice().sort((a, b) => a - b),
            selectedHours: hours.slice().sort((a, b) => a - b),
            selectedMinutes: minutes.slice().sort((a, b) => a - b),
        });
    });

    // Join with pipe separator
    return cronStrings.join("|");
}

// ─── Preview Dates ────────────────────────────────────────────────────────────

export interface PreviewDatesParams {
    activeTab: ActiveTab;
    startDate: string;
    startTime: string;
    timeDateValue: Date | null;
    useMonthFilter: boolean;
    selectedMonths: number[];
    useDayFilter: boolean;
    selectedDays: number[];
    useDayOfMonthFilter: boolean;
    selectedDaysOfMonth: number[];
    baseInterval: number;
    baseUnit: BaseUnit;
    /** When present, override day/time filters with the chips data. */
    scheduledRestrictions?: ScheduledRestriction[];
}

export function computePreviewDates(params: PreviewDatesParams): Date[] {
    const {
        activeTab, startDate, startTime, timeDateValue,
        useMonthFilter, selectedMonths,
        useDayFilter, selectedDays,
        useDayOfMonthFilter, selectedDaysOfMonth,
        baseInterval, baseUnit,
        scheduledRestrictions = [],
    } = params;

    const [year, month, day] = startDate.split("-").map(Number);
    const [hour, minute] = startTime.split(":").map(Number);
    const dates: Date[] = [];

    if (activeTab === "single") {
        dates.push(timeDateValue ? new Date(timeDateValue) : new Date(year, month - 1, day, hour, minute));
        return dates;
    }

    // ── Chip-based preview ───────────────────────────────────────────────────
    if (scheduledRestrictions.length > 0) {
        return computePreviewDatesFromChips(scheduledRestrictions, startDate);
    }

    // ── Legacy preview ───────────────────────────────────────────────────────
    let cursor = new Date(year, month - 1, day, hour, minute);
    const interval = Math.max(1, baseInterval);
    let found = 0;
    let safety = 0;

    while (found < 10 && safety < 5000) {
        safety++;
        const valid =
            (!useMonthFilter || selectedMonths.length === 0 || selectedMonths.includes(cursor.getMonth() + 1)) &&
            (!useDayFilter || selectedDays.length === 0 || selectedDays.includes(cursor.getDay())) &&
            (!useDayOfMonthFilter || selectedDaysOfMonth.length === 0 || selectedDaysOfMonth.includes(cursor.getDate()));

        if (valid) { dates.push(new Date(cursor)); found++; }

        if (useDayFilter || useDayOfMonthFilter) {
            cursor.setDate(cursor.getDate() + 1);
        } else {
            if (baseUnit === "minutes") cursor.setMinutes(cursor.getMinutes() + interval);
            else if (baseUnit === "hours") cursor.setHours(cursor.getHours() + interval);
            else if (baseUnit === "days") cursor.setDate(cursor.getDate() + interval);
            else if (baseUnit === "weeks") cursor.setDate(cursor.getDate() + interval * 7);
            else if (baseUnit === "months") cursor.setMonth(cursor.getMonth() + interval);
        }
    }

    return dates;
}

export function computePreviewDatesFromChips(
    chips: ScheduledRestriction[],
    startDateStr: string
): Date[] {
    if (chips.length === 0) return [];

    const [year, month, day] = startDateStr.split("-").map(Number);
    const startBase = new Date(year, month - 1, day, 0, 0, 0);

    const allDates: Date[] = [];

    for (const chip of chips) {
        const [h, m] = chip.time.split(":").map(Number);
        const { useFrequency, baseInterval, baseUnit } = chip;
        
        let cursor = new Date(startBase);
        
        // If frequency is used but no days are filtered, start the cursor at the specific time
        if (useFrequency && chip.days.length === 0 && chip.daysOfMonth.length === 0 && chip.months.length === 0) {
            cursor = new Date(year, month - 1, day, h, m, 0);
        }

        let found = 0;
        let safety = 0;
        const interval = Math.max(1, baseInterval || 1);

        while (found < 10 && safety < 1000) {
            safety++;
            const matchDay = chip.days.length === 0 || chip.days.includes(cursor.getDay());
            const matchDom = chip.daysOfMonth.length === 0 || chip.daysOfMonth.includes(cursor.getDate());
            const matchMonth = chip.months.length === 0 || chip.months.includes(cursor.getMonth() + 1);

            if (matchDay && matchDom && matchMonth) {
                // If it's a frequency match, the cursor itself is the next time (except for first day check)
                if (useFrequency && (chip.days.length === 0 && chip.daysOfMonth.length === 0 && chip.months.length === 0)) {
                    allDates.push(new Date(cursor));
                } else if (!useFrequency) {
                    allDates.push(new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), h, m));
                } else {
                    // With frequency AND days, we generate periods through the matching day
                    let dayCursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), h, m);
                    // Generate until end of day
                    const endOfDay = new Date(dayCursor);
                    endOfDay.setHours(23, 59, 59);
                    
                    while (dayCursor <= endOfDay && found < 10) {
                        allDates.push(new Date(dayCursor));
                        found++;
                        if (baseUnit === "minutes") dayCursor.setMinutes(dayCursor.getMinutes() + interval);
                        else if (baseUnit === "hours") dayCursor.setHours(dayCursor.getHours() + interval);
                        else break; // only hours/minutes are granular enough for daily repetition
                    }
                    // Subtract 1 since we already incremented `found` inside the loop
                    found--;
                }
                found++;
            }

            // Step cursor
            if (useFrequency && chip.days.length === 0 && chip.daysOfMonth.length === 0 && chip.months.length === 0) {
                if (baseUnit === "minutes") cursor.setMinutes(cursor.getMinutes() + interval);
                else if (baseUnit === "hours") cursor.setHours(cursor.getHours() + interval);
                else if (baseUnit === "days") cursor.setDate(cursor.getDate() + interval);
                else if (baseUnit === "weeks") cursor.setDate(cursor.getDate() + interval * 7);
                else if (baseUnit === "months") cursor.setMonth(cursor.getMonth() + interval);
            } else {
                // Advance by day
                cursor.setDate(cursor.getDate() + 1);
                if (!useFrequency) {
                    cursor.setHours(h, m, 0, 0); // Keep time normalized if just checking days
                } else {
                    cursor.setHours(0, 0, 0, 0); // Keep normalized to start of day since we generate frequency inside the day
                }
            }
        }
    }

    // Sort and deduplicate by timestamp
    const unique = Array.from(new Map(allDates.map((d) => [d.getTime(), d])).values());
    unique.sort((a, b) => a.getTime() - b.getTime());
    return unique.slice(0, 10);
}

// ─── Crondata Updater (hours/minutes from UI state) ───────────────────────────

export function computeUpdatedCronHoursMinutes(
    startTime: string,
    baseUnit: BaseUnit,
    baseInterval: number,
    useFrequency: boolean
): { hours: number[]; minutes: number[]; every: EverySupported } {
    const [h, m] = startTime.split(":").map(Number);
    const interval = useFrequency ? Math.max(1, baseInterval) : 1;

    let newHours = [h];
    let newMinutes = [m];

    if (useFrequency) {
        if (baseUnit === "hours") {
            newHours = [];
            for (let i = h; i < 24; i += interval) newHours.push(i);
        } else if (baseUnit === "minutes") {
            newMinutes = [];
            for (let i = m; i < 60; i += interval) newMinutes.push(i);
        }
    }

    const every = !useFrequency ? EverySupported.minute :
        baseUnit === "months" ? EverySupported.year :
            baseUnit === "weeks" ? EverySupported.month :
                baseUnit === "days" ? EverySupported.week :
                    baseUnit === "hours" ? EverySupported.day : EverySupported.hour;

    return { hours: newHours, minutes: newMinutes, every };
}